import { useTranslation } from "react-i18next";
import { ExternalLink } from "lucide-react";
import { RatingStars } from "@/features/reports/components/RatingStars";
import { cn } from "@/lib/cn";
import type { FeedbackRow } from "@/types/feedback";

interface Props {
  row: FeedbackRow;
  /** Opens the order detail modal for this row's order. */
  onOpenOrder: (orderId: number) => void;
  /**
   * IANA timezone for the submission timestamp (TASK-5043). Supplied by
   * `ReviewsTab` through `FeedbackList`; omitted → the browser zone.
   */
  tenantTz?: string;
}

/**
 * Status badge styles, keyed by order status. Mirrors the map in
 * OrdersReportTable so badges look identical across reports and feedback.
 * Unknown statuses fall back to a neutral gray badge.
 */
const STATUS_STYLE: Record<
  string,
  { bg: string; text: string; dot: string; i18nKey: string }
> = {
  new: {
    bg: "bg-blue-50",
    text: "text-blue-700",
    dot: "bg-blue-400",
    i18nKey: "orders.status.new",
  },
  in_route: {
    bg: "bg-orange-50",
    text: "text-orange-700",
    dot: "bg-orange-400",
    i18nKey: "orders.status.in_route",
  },
  pending: {
    bg: "bg-amber-50",
    text: "text-amber-700",
    dot: "bg-amber-400",
    i18nKey: "orders.status.pending",
  },
  visited: {
    bg: "bg-sky-50",
    text: "text-sky-700",
    dot: "bg-sky-400",
    i18nKey: "orders.status.visited",
  },
  confirmed: {
    bg: "bg-emerald-50",
    text: "text-emerald-700",
    dot: "bg-emerald-500",
    i18nKey: "orders.status.confirmed",
  },
  rejected: {
    bg: "bg-rose-50",
    text: "text-rose-700",
    dot: "bg-rose-500",
    i18nKey: "orders.status.rejected",
  },
  skipped: {
    bg: "bg-amber-50",
    text: "text-amber-700",
    dot: "bg-amber-400",
    i18nKey: "orders.status.skipped",
  },
  archived: {
    bg: "bg-gray-100",
    text: "text-gray-600",
    dot: "bg-gray-400",
    i18nKey: "orders.status.archived",
  },
};

const NEUTRAL_STATUS = {
  bg: "bg-gray-100",
  text: "text-gray-600",
  dot: "bg-gray-400",
};

/**
 * The feedback's submission instant, in the TENANT's zone (TASK-5043).
 *
 * `tenantTz` is threaded down from `ReviewsTab` (which already holds it) rather
 * than read from `useTenantTimezone()` here: this card's test renders it with no
 * `QueryClientProvider`, and the hook throws without one — the same reason
 * `AnalyticsFilterBar` takes a prop. Omitted → the browser zone, as before.
 *
 * The `ru-RU`/`en-GB` locale pinning is pre-existing: it forces a 24-hour clock
 * regardless of the UI language's region.
 */
function formatDate(iso: string, locale: string, tenantTz?: string): string {
  return new Date(iso).toLocaleString(locale === "ru" ? "ru-RU" : "en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    timeZone: tenantTz,
  });
}

/**
 * One recipient-feedback entry rendered as a card (TASK-3814). The header shows
 * the rating, a clickable order reference, the driver, the date, and a status
 * badge; the full comment is rendered below in a quoted block (never truncated)
 * or a muted "no comment" line when empty.
 */
export function FeedbackCard({ row, onOpenOrder, tenantTz }: Props) {
  const { t, i18n } = useTranslation();

  const reference = row.reference ?? row.external_id ?? `#${row.order_id}`;
  const st = STATUS_STYLE[row.status];
  const statusLabel = st ? t(st.i18nKey) : row.status;

  return (
    <div className="rounded-2xl border border-gray-100 bg-white p-4 shadow-sm">
      {/* Header row */}
      <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
        {/* Rating */}
        <span className="inline-flex items-center gap-1.5">
          <RatingStars value={row.rating} size={16} />
          <span className="text-sm font-semibold text-gray-700">
            {row.rating}
          </span>
        </span>

        {/* Order reference — opens the order detail modal */}
        <button
          type="button"
          onClick={() => onOpenOrder(row.order_id)}
          className="inline-flex items-center gap-1 text-sm font-semibold text-brand-primary transition-colors hover:text-blue-600"
          title={t("feedback.card.openOrder")}
        >
          <span className="max-w-[220px] truncate">{reference}</span>
          <ExternalLink size={13} className="shrink-0" />
        </button>

        {/* Driver */}
        <span className="text-sm text-gray-500">
          {row.driver_name ? (
            row.driver_name
          ) : (
            <span className="text-gray-300">—</span>
          )}
        </span>

        {/* Spacer pushes date + status to the right */}
        <div className="ml-auto flex items-center gap-3">
          <span className="text-xs text-gray-400">
            {formatDate(row.created_at, i18n.language, tenantTz)}
          </span>
          <span
            className={cn(
              "inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-xs font-semibold",
              st ? st.bg : NEUTRAL_STATUS.bg,
              st ? st.text : NEUTRAL_STATUS.text,
            )}
          >
            <span
              className={cn(
                "h-1.5 w-1.5 rounded-full",
                st ? st.dot : NEUTRAL_STATUS.dot,
              )}
            />
            {statusLabel}
          </span>
        </div>
      </div>

      {/* Address */}
      {row.address ? (
        <p className="mt-1.5 text-xs text-gray-400">{row.address}</p>
      ) : null}

      {/* Comment */}
      {row.comment ? (
        <blockquote className="mt-3 whitespace-pre-line break-words border-l-2 border-gray-200 pl-3 text-sm italic text-gray-700">
          {row.comment}
        </blockquote>
      ) : (
        <p className="mt-3 text-sm italic text-gray-300">
          {t("feedback.card.noComment")}
        </p>
      )}
    </div>
  );
}
